<template>
  <div>
    <SettingGroup title="Bluetooth機器のボタン割り当て">
      <div class="blekey-notice">
        <p>各Bluetooth機器のボタン数およびボタン配置については、マニュアルを確認してください。</p>
        <p>ボタン１は PTT 固定のため、設定を変更できません。</p>
      </div>

      <div class="blekey-table">
        <div class="blekey-table-header blekey-table-header-spacer"></div>
        <div class="blekey-table-header">ファンクション</div>
        <div class="blekey-table-header">ホールド・ファンクション</div>

        <template v-for="button in bleButtons">
          <div :key="`${button.formKey}-label`" class="blekey-button-label">
            {{ button.label }}
          </div>

          <div :key="`${button.formKey}-function`" class="blekey-cell">
            <div class="blekey-mobile-label">ファンクション</div>
            <SelectBox
              :disabled="functionSelectDisable[button.formKey]"
              :options="selectboxOptions[button.formKey].function"
              :defaultValue="String(form.selectbox[button.formKey].function)"
              :width="260"
              @update="(value) => handleSelectFunction(button.formKey, value)"
            />
          </div>

          <div :key="`${button.formKey}-hold`" class="blekey-cell">
            <div class="blekey-mobile-label">ホールド・ファンクション</div>
            <SelectBox
              :disabled="holdFunctionSelectDisable[button.formKey]"
              :options="selectboxOptions[button.formKey].holdFunction"
              :defaultValue="String(form.selectbox[button.formKey].holdFunction)"
              :width="260"
              @update="(value) => handleSelectHoldFunction(button.formKey, value)"
            />
          </div>
        </template>
      </div>
    </SettingGroup>
  </div>
</template>

<script>
import SelectBox from '@/components/pc/radio/ipradio/atoms/SelectBox.vue'
import SettingGroup from '@/components/pc/radio/ipradio/organisms/layout/SettingGroup.vue'
import { BC_TRANS_JP, FUNCTION_LIST } from '@/components/pc/radio/ipradio/config/BCconstants'
import { useRadioSettingsStore } from '@/components/pc/radio/ipradio/stores/bcRadioSettings'
import { mapStores } from 'pinia'

const BLE_BUTTON_LABELS = BC_TRANS_JP.KEY_ASSIGNMENT.GENERAL.EMERGENCY.BLE_BUTTON
const BLE_BUTTONS = Object.freeze([
  { formKey: 'button2', settingKey: 'Button2', label: BLE_BUTTON_LABELS.BUTTON2 },
  { formKey: 'button3', settingKey: 'Button3', label: BLE_BUTTON_LABELS.BUTTON3 },
  { formKey: 'button4', settingKey: 'Button4', label: BLE_BUTTON_LABELS.BUTTON4 },
  { formKey: 'button5', settingKey: 'Button5', label: BLE_BUTTON_LABELS.BUTTON5 },
])

export default {
  name: 'BLEKeyAssignmentVue2',
  components: { SelectBox, SettingGroup },
  data () {
    const baseOptions = (filterFn) =>
      FUNCTION_LIST.map(ele => {
        if (filterFn(ele)) return { label: ele.label, value: ele.value }
      }).filter(Boolean)

    const initialOptions = {}
    BLE_BUTTONS.forEach((button) => {
      initialOptions[button.formKey] = {
          function: baseOptions(ele => ele.type.key && ele.function && ele.function.function),
          holdFunction: baseOptions(ele => ele.type.key && ele.function && ele.function.holdFunction),
      }
    })

    const formSelectbox = {}
    BLE_BUTTONS.forEach((button) => {
      formSelectbox[button.formKey] = {
        function: initialOptions[button.formKey].function[0]?.label || 'None',
        holdFunction: initialOptions[button.formKey].holdFunction[0]?.label || 'None',
      }
    })

    const functionSelectDisable = {}
    BLE_BUTTONS.forEach((button) => {
      functionSelectDisable[button.formKey] = false
    })

    const holdFunctionSelectDisable = {}
    BLE_BUTTONS.forEach((button) => {
      holdFunctionSelectDisable[button.formKey] = false
    })

    return {
      bleButtons: BLE_BUTTONS,
      selectboxOptions: initialOptions,
      form: {
        selectbox: formSelectbox,
      },
      setting: null,
      functionSelectDisable,
      holdFunctionSelectDisable,
    }
  },
  computed: {
    ...mapStores(useRadioSettingsStore),
  },
  created () {
    // ストアから設定取得
    this.setting = this.radioSettingsStore.getTempSetting()

    // 設定 → フォーム
    this.transSetting2Form()

    // 選択肢の初期再構成
    BLE_BUTTONS.forEach((button) => {
      this.updateSelectboxOptions(button.formKey)

      if (this.form.selectbox[button.formKey].function === 'SecondPTT') {
        this.holdFunctionSelectDisable[button.formKey] = true
      }
    })

    this.applyEmergencyBleLock()

    // フォーム変更を監視して 設定 ← フォーム & 保存
    this.$watch('form', () => {
      this.transForm2Setting()
      this.radioSettingsStore.saveTempSetting(this.setting)
    }, { deep: true })
  },
  methods: {
    getSettingKey (formKey) {
      const button = this.bleButtons.find((item) => item.formKey === formKey)
      return button ? button.settingKey : ''
    },

    applyEmergencyBleLock () {
      const emergency = this.setting?.KeyAssignment?.General?.Emergency
      const isEmergencyBleEnabled = emergency?.BLE === 'Enable'
      const targetButton = emergency?.BLEButton || ''

      this.bleButtons.forEach((button) => {
        const isTargetButton = button.settingKey === targetButton

        this.functionSelectDisable[button.formKey] = isEmergencyBleEnabled && isTargetButton
        this.holdFunctionSelectDisable[button.formKey] =
          (this.form.selectbox[button.formKey].function === 'SecondPTT') ||
          (isEmergencyBleEnabled && isTargetButton)
      })
    },

    // ====== 設定 ← フォーム ======
    transForm2Setting () {
      this.buttonTransForm2Setting('button2')
      this.buttonTransForm2Setting('button3')
      this.buttonTransForm2Setting('button4')
      this.buttonTransForm2Setting('button5')
    },
    buttonTransForm2Setting (formKey) {
      const data = this.setting?.KeyAssignment?.BLEKey
      if (!data) return

      const settingKey = this.getSettingKey(formKey)
      if (!settingKey || !data[settingKey]) return

      const func = this.form.selectbox[formKey].function
      const hold = this.form.selectbox[formKey].holdFunction

      const isPressRelease = func === 'SecondPTT'
      const isPress =
        hold === 'None' ||
        (func === 'GroupDown' && hold === 'GroupDown(Continuous)') ||
        (func === 'GroupUp' && hold === 'GroupUp(Continuous)')

      data[settingKey]['Function(Press)'] = (isPressRelease || isPress) ? func : 'None'
      data[settingKey]['Function(Release)'] = (isPressRelease || !isPress) ? func : 'None'
      data[settingKey].HoldFunction = hold
    },

    // ====== 設定 → フォーム ======
    transSetting2Form () {
      this.buttonTransSetting2Form('button2')
      this.buttonTransSetting2Form('button3')
      this.buttonTransSetting2Form('button4')
      this.buttonTransSetting2Form('button5')
    },
    buttonTransSetting2Form (formKey) {
      const data = this.setting?.KeyAssignment?.BLEKey
      if (!data) return

      const settingKey = this.getSettingKey(formKey)
      if (!settingKey || !data[settingKey]) return

      const press = data[settingKey]['Function(Press)']
      const hold = data[settingKey].HoldFunction

      const isPress =
        hold === 'None' ||
        (press === 'GroupDown' && hold === 'GroupDown(Continuous)') ||
        (press === 'GroupUp' && hold === 'GroupUp(Continuous)')

      this.form.selectbox[formKey].function = isPress
        ? data[settingKey]['Function(Press)']
        : data[settingKey]['Function(Release)']
      this.form.selectbox[formKey].holdFunction = hold
    },

    // ====== 選択肢の再構成 ======
    updateSelectboxOptions (formKey) {
      const menuData = this.setting?.KeyAssignment?.Menu || []
      const extendedMenuData = this.setting?.KeyAssignment?.ExtendedMenu || []

      const hasMenuAutoBacklight =
        menuData.some(item => (item.Function || []).includes('AutoBacklight')) ||
        extendedMenuData.some(item => (item.Function || []).includes('AutoBacklight'))

      const formFunction = this.form.selectbox[formKey].function
      const formHoldFunction = this.form.selectbox[formKey].holdFunction

      // function
      this.selectboxOptions[formKey].function = FUNCTION_LIST.map(ele => {
        if (
          ele.type.key &&
          ele.function?.function &&
          !(hasMenuAutoBacklight && ele.value === 'Backlight')
        ) {
          return { label: ele.label, value: ele.value }
        }
      }).filter(Boolean)

      // holdFunction
      this.selectboxOptions[formKey].holdFunction = FUNCTION_LIST.map(ele => {
        if (
          (ele.type.key &&
            ele.function?.holdFunction &&
            !(hasMenuAutoBacklight && ele.value === 'Backlight')) ||
          (formFunction === 'GroupDown' && ele.value === 'GroupDown(Continuous)') ||
          (formFunction === 'GroupUp' && ele.value === 'GroupUp(Continuous)') ||
          (formHoldFunction === 'Emergency' && ele.value === 'Emergency')
        ) {
          return { label: ele.label, value: ele.value }
        }
      }).filter(Boolean)
    },

    // ====== ハンドラ ======
    handleSelectFunction (formKey, value) {
      this.form.selectbox[formKey].function = value

      const holdDefault = FUNCTION_LIST.find(ele => ele.value === value)?.function?.holdFunctionDefault
      if (holdDefault) {
        this.form.selectbox[formKey].holdFunction = holdDefault
      }

      // SecondPTT 時はホールドを無効
      this.holdFunctionSelectDisable[formKey] = (this.form.selectbox[formKey].function === 'SecondPTT')

      this.updateSelectboxOptions(formKey)
      this.applyEmergencyBleLock()
    },
    handleSelectHoldFunction (formKey, value) {
      this.form.selectbox[formKey].holdFunction = value
    },
  },
}
</script>

<style scoped>
.blekey-notice {
  margin-bottom: 16px;
  color: #666;
  line-height: 1.6;
}

.blekey-notice p {
  margin: 0;
}

.blekey-table {
  display: grid;
  grid-template-columns: 120px minmax(260px, 1fr) minmax(260px, 1fr);
  gap: 16px;
  align-items: center;
}

.blekey-table-header {
  color: #888;
  font-size: 12px;
  font-weight: 600;
}

.blekey-button-label {
  font-weight: 600;
}

.blekey-cell {
  min-width: 0;
}

.blekey-mobile-label {
  display: none;
  margin-bottom: 6px;
  color: #888;
  font-size: 12px;
  font-weight: 600;
}

@media (max-width: 960px) {
  .blekey-table {
    grid-template-columns: 1fr;
  }

  .blekey-table-header,
  .blekey-table-header-spacer {
    display: none;
  }

  .blekey-mobile-label {
    display: block;
  }
}
</style>
